import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { utils } from '../../utils';
import {
  buscarEtiqueta,
  criarPedido,
  editarPedido,
  Etiqueta,
  EtiquetaStatus,
  STATUS_LABELS
} from '../../services/etiquetas';
import { listarClientes, Cliente } from '../../services/clientes';

const TODOS_STATUS: EtiquetaStatus[] = [
  'pendente', 'pre_carregamento', 'carregada', 'em_transito',
  'descarregada', 'conferida', 'divergente', 'com_ocorrencia'
];

const EtiquetaForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [form, setForm] = useState({
    cliente_id: '',
    numero_pedido: '',
    numero_nfe: '',
    serie_nfe: '',
    data_emissao: '',
    valor_nfe: '',
    peso_total: '',
    volumes: '',
    cubagem: '',
    chave_nfe: '',
    emitente_cnpj: '',
    emitente_nome: '',
    destinatario_cnpj: '',
    destinatario_nome: '',
    dest_logradouro: '',
    dest_numero: '',
    dest_bairro: '',
    dest_municipio: '',
    dest_uf: '',
    dest_cep: '',
    codigo_barras: '',
    volume_atual: '',
    total_volumes_pedido: '',
    status: 'pendente' as EtiquetaStatus
  });

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const result = await listarClientes({ limit: 200, ativo: 'true' });
        if (result.success) setClientes(result.clientes);
      } catch {
        // Silencioso - clientes são carregados no select
      }
    };
    fetchClientes();
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    const fetchEtiqueta = async () => {
      try {
        const response = await buscarEtiqueta(Number(id));
        if (response.success) {
          const e = response.dados;
          setForm({
            cliente_id: e.cliente_id ? String(e.cliente_id) : '',
            numero_pedido: e.numero_pedido || '',
            numero_nfe: e.numero_nfe || '',
            serie_nfe: e.serie_nfe || '',
            data_emissao: e.data_emissao ? e.data_emissao.substring(0, 10) : '',
            valor_nfe: e.valor_nfe !== null && e.valor_nfe !== undefined ? String(e.valor_nfe) : '',
            peso_total: e.peso_total !== null && e.peso_total !== undefined ? String(e.peso_total) : '',
            volumes: e.volumes !== null && e.volumes !== undefined ? String(e.volumes) : '',
            cubagem: e.cubagem !== null && e.cubagem !== undefined ? String(e.cubagem) : '',
            chave_nfe: e.chave_nfe || '',
            emitente_cnpj: e.emitente_cnpj || '',
            emitente_nome: e.emitente_nome || '',
            destinatario_cnpj: e.destinatario_cnpj || '',
            destinatario_nome: e.destinatario_nome || '',
            dest_logradouro: e.dest_logradouro || '',
            dest_numero: e.dest_numero || '',
            dest_bairro: e.dest_bairro || '',
            dest_municipio: e.dest_municipio || '',
            dest_uf: e.dest_uf || '',
            dest_cep: e.dest_cep || '',
            codigo_barras: e.codigo_barras || '',
            volume_atual: e.volume_atual !== null && e.volume_atual !== undefined ? String(e.volume_atual) : '',
            total_volumes_pedido: e.total_volumes_pedido !== null && e.total_volumes_pedido !== undefined ? String(e.total_volumes_pedido) : '',
            status: e.status
          });
        }
      } catch (error: any) {
        utils.showNotification(error.message || 'Erro ao carregar pedido', { type: 'error' });
        navigate('/pedidos');
      } finally {
        setIsLoading(false);
      }
    };
    fetchEtiqueta();
  }, [id, isEditing, navigate]);

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.cliente_id) {
      utils.showNotification('Selecione um cliente', { type: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      const parseNum = (val: string): number | undefined => {
        if (!val) return undefined;
        const n = parseFloat(val);
        return isNaN(n) ? undefined : n;
      };
      const parseInt_ = (val: string): number | undefined => {
        if (!val) return undefined;
        const n = parseInt(val, 10);
        return isNaN(n) ? undefined : n;
      };

      const payload: Partial<Etiqueta> = {
        cliente_id: parseInt(form.cliente_id),
        numero_pedido: form.numero_pedido || undefined,
        numero_nfe: form.numero_nfe || undefined,
        serie_nfe: form.serie_nfe || undefined,
        data_emissao: form.data_emissao || undefined,
        valor_nfe: parseNum(form.valor_nfe),
        peso_total: parseNum(form.peso_total),
        volumes: parseInt_(form.volumes),
        cubagem: parseNum(form.cubagem),
        chave_nfe: form.chave_nfe || undefined,
        emitente_cnpj: form.emitente_cnpj || undefined,
        emitente_nome: form.emitente_nome || undefined,
        destinatario_cnpj: form.destinatario_cnpj || undefined,
        destinatario_nome: form.destinatario_nome || undefined,
        dest_logradouro: form.dest_logradouro || undefined,
        dest_numero: form.dest_numero || undefined,
        dest_bairro: form.dest_bairro || undefined,
        dest_municipio: form.dest_municipio || undefined,
        dest_uf: form.dest_uf || undefined,
        dest_cep: form.dest_cep || undefined,
        codigo_barras: form.codigo_barras || undefined,
        volume_atual: parseInt_(form.volume_atual),
        total_volumes_pedido: parseInt_(form.total_volumes_pedido),
        status: form.status
      };

      let response;
      if (isEditing) {
        response = await editarPedido(Number(id), payload);
      } else {
        response = await criarPedido(payload);
      }

      if (response.success) {
        utils.showNotification(response.message, { type: 'success' });
        navigate('/pedidos');
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao salvar pedido', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="page-container" style={{ textAlign: 'center', padding: '60px' }}>
        <div className="loading-spinner-large"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>
          <i className={`fas ${isEditing ? 'fa-edit' : 'fa-plus-circle'}`}></i>{' '}
          {isEditing ? 'Editar Pedido' : 'Novo Pedido'}
        </h1>
        <button className="btn btn-outline" onClick={() => navigate('/pedidos')}>
          <i className="fas fa-arrow-left"></i> Voltar
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ padding: '24px', marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
            Dados Gerais
          </h3>
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">Cliente *</label>
              <select className="form-control" value={form.cliente_id} onChange={e => handleChange('cliente_id', e.target.value)} required>
                <option value="">Selecione...</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.razao_social}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">N. do Pedido</label>
              <input className="form-control" value={form.numero_pedido} onChange={e => handleChange('numero_pedido', e.target.value)} placeholder="Ex: PED-001" />
            </div>
            {!isEditing && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" value={form.status} onChange={e => handleChange('status', e.target.value)}>
                  {TODOS_STATUS.map(s => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Cod. Barras</label>
              <input className="form-control" value={form.codigo_barras} onChange={e => handleChange('codigo_barras', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: '24px', marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            <i className="fas fa-file-invoice" style={{ marginRight: '8px' }}></i>
            Dados da Nota Fiscal
          </h3>
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">N. NF-e</label>
              <input className="form-control" value={form.numero_nfe} onChange={e => handleChange('numero_nfe', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Série</label>
              <input className="form-control" value={form.serie_nfe} onChange={e => handleChange('serie_nfe', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Data Emissão</label>
              <input className="form-control" type="date" value={form.data_emissao} onChange={e => handleChange('data_emissao', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Valor NF-e (R$)</label>
              <input className="form-control" type="number" step="0.01" value={form.valor_nfe} onChange={e => handleChange('valor_nfe', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Chave NF-e</label>
              <input className="form-control" value={form.chave_nfe} onChange={e => handleChange('chave_nfe', e.target.value)} maxLength={44} />
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: '24px', marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            <i className="fas fa-exchange-alt" style={{ marginRight: '8px' }}></i>
            Emitente / Destinatário
          </h3>
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">Emitente (Nome)</label>
              <input className="form-control" value={form.emitente_nome} onChange={e => handleChange('emitente_nome', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">CNPJ Emitente</label>
              <input className="form-control" value={form.emitente_cnpj} onChange={e => handleChange('emitente_cnpj', e.target.value)} maxLength={14} placeholder="Apenas números" />
            </div>
            <div className="form-group">
              <label className="form-label">Destinatário (Nome)</label>
              <input className="form-control" value={form.destinatario_nome} onChange={e => handleChange('destinatario_nome', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">CNPJ/CPF Destinatário</label>
              <input className="form-control" value={form.destinatario_cnpj} onChange={e => handleChange('destinatario_cnpj', e.target.value)} maxLength={14} placeholder="Apenas números" />
            </div>
          </div>

          <h4 style={{ fontSize: '14px', color: '#6b7280', margin: '20px 0 12px' }}>
            <i className="fas fa-map-marker-alt" style={{ marginRight: '6px' }}></i>
            Endereço de Destino
          </h4>
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">Logradouro</label>
              <input className="form-control" value={form.dest_logradouro} onChange={e => handleChange('dest_logradouro', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Número</label>
              <input className="form-control" value={form.dest_numero} onChange={e => handleChange('dest_numero', e.target.value)} maxLength={20} />
            </div>
            <div className="form-group">
              <label className="form-label">Bairro</label>
              <input className="form-control" value={form.dest_bairro} onChange={e => handleChange('dest_bairro', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Município</label>
              <input className="form-control" value={form.dest_municipio} onChange={e => handleChange('dest_municipio', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">UF</label>
              <input className="form-control" value={form.dest_uf} onChange={e => handleChange('dest_uf', e.target.value)} maxLength={2} placeholder="Ex: MG" />
            </div>
            <div className="form-group">
              <label className="form-label">CEP</label>
              <input className="form-control" value={form.dest_cep} onChange={e => handleChange('dest_cep', e.target.value)} maxLength={8} placeholder="Apenas números" />
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: '24px', marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            <i className="fas fa-box" style={{ marginRight: '8px' }}></i>
            Logística
          </h3>
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">Peso Total (kg)</label>
              <input className="form-control" type="number" step="0.001" value={form.peso_total} onChange={e => handleChange('peso_total', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Volumes</label>
              <input className="form-control" type="number" value={form.volumes} onChange={e => handleChange('volumes', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Cubagem (m³)</label>
              <input className="form-control" type="number" step="0.001" value={form.cubagem} onChange={e => handleChange('cubagem', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Volume Atual</label>
              <input className="form-control" type="number" value={form.volume_atual} onChange={e => handleChange('volume_atual', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Total Volumes Pedido</label>
              <input className="form-control" type="number" value={form.total_volumes_pedido} onChange={e => handleChange('total_volumes_pedido', e.target.value)} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button type="button" className="btn btn-outline" onClick={() => navigate('/pedidos')} disabled={isSaving}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSaving}>
            {isSaving ? (
              <><div className="loading-spinner-small" style={{ marginRight: '8px' }}></div> Salvando...</>
            ) : (
              <><i className={`fas ${isEditing ? 'fa-save' : 'fa-plus'}`}></i> {isEditing ? 'Salvar' : 'Criar Pedido'}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EtiquetaForm;
